import { createFeature, createReducer, on } from '@ngrx/store';
import {
  deflectorPlayerNumberChange,
  detectionsChange,
  eventIdChange,
  eventTypeChange,
  faceoffOpponentPlayerNumberChange,
  faceoffOutcomePositionChange,
  assist1PlayerNumberChange,
  fouledPlayerNumberChange,
  turnoverOpponentPlayerNumberChange,
  official1NumberChange,
  official2NumberChange,
  gameTimeChange,
  gameIncidentReasonChange,
  gameIncidentSituationChange,
  gameIncidentTypeChange,
  hasNetTrafficChange,
  hasScreenChange,
  highlightPlaybackChange,
  highlightRatingChange,
  highlightTypeChange,
  interruptionTypeChange,
  isAwayTeamEmptyNetChange,
  isBlockedChange,
  isHomeTeamEmptyNetChange,
  netImpactChange,
  netTrafficCauserPlayerNumberChange,
  oddMenRushDetailChange,
  officialsCallActionChange,
  officialsCallSituationChange,
  passOutcomeChange,
  passReceiverPlayerNumberChange,
  passReceiverPositionChange,
  passTypeChange,
  penaltyIdChange,
  penaltyDurationChange,
  penaltyTypeChange,
  periodChange,
  playerNumberChange,
  positionChange,
  positionPitchChange,
  resetState,
  resetHardState,
  screenerPlayerNumberChange,
  assist2PlayerNumberChange,
  selectedTrackIdChange,
  shotBlockerPlayerNumberChange,
  shotBlockerPositionChange,
  shotOutcomeChange,
  shotScenarioChange,
  shotTypeChange,
  strengthStateChange,
  teamChange,
  teamFaceoffOutcomeChange,
  videoTagChange,
  defensiveReactionChange,
  gameRestartTypeChange,
  gameRestartReasonChange,
  turnoverTypeChange,
  turnoverScenarioChange,
  videoTimeChange,
  videoReviewTypeChange,
  attackScenarioChange
} from '../actions/game-event.action';
import {
  AttackScenario,
  DefensiveReaction,
  EventType,
  GameIncidentReason,
  GameIncidentSituation,
  GameIncidentType,
  GamePeriod,
  HighlightPlayback,
  HighlightRating,
  HighlightType,
  InterruptionType,
  NetImpactPositionCoordinate,
  OddMenRushDetail,
  OfficialsCallAction,
  OfficialsCallSituation,
  PassOutcome,
  PassType,
  PenaltyDuration,
  PenaltyType,
  PositionCoordinate,
  ShotOutcome,
  ShotScenario,
  ShotType,
  StrengthState,
  TeamFaceOffOutcome,
  VideoReviewType,
  VideoTagType
} from '../../domain/game-event';
import { GameRestartReason, GameRestartType } from '../../domain/game-restart';
import { PitchId } from '../../domain/pitch';
import { TurnoverScenario, TurnoverType } from '../../domain/turnover';
import { Detection } from '../../services/detection';
import ObjectId from 'bson-objectid';

export const gameEventFeatureKey = 'gameEvent';

export interface OfficialStateAction {
  officialId: number;
  remove: boolean;
}

export interface GameEventState {
  _id: string;
  videoTime: number;
  gameTime: number;
  period: GamePeriod;
  strengthState: StrengthState;
  eventType: EventType;
  team: string;
  isHomeTeamEmptyNet: boolean;
  isAwayTeamEmptyNet: boolean;
  oddMenRushDetail: OddMenRushDetail;

  position: PositionCoordinate;
  positionPitch: PitchId;
  passReceiverPosition: PositionCoordinate;
  shotBlockerPosition: PositionCoordinate;
  faceoffOutcomePosition: PositionCoordinate;

  passType: PassType;
  passOutcome: PassOutcome;

  shotOutcome: ShotOutcome;
  shotScenario: ShotScenario;
  shotType: ShotType;
  attackScenario: AttackScenario;

  is_blocked: string;
  assist1: string;
  assist1Id: string;
  assist2: string;
  assist2Id: string;
  blocker: string;
  blockerId: string;
  blockerXPosition: number;
  blockerYPosition: number;
  has_net_traffic: string;
  has_screen: string;

  teamFaceOffOutcome: TeamFaceOffOutcome;

  interruption_type: InterruptionType;

  videoTag: VideoTagType;
  defensiveReaction: DefensiveReaction;
  gameRestartType: GameRestartType;
  gameRestartReason: GameRestartReason;
  turnoverType: TurnoverType;
  turnoverScenario: TurnoverScenario;

  playerNumber: string;
  passReceiverPlayerNumber: string;
  shotBlockerPlayerNumber: string;
  deflectorPlayerNumber: string;
  screenerPlayerNumber: string;
  netTrafficCauserPlayerNumber: string;
  faceOffOpponentPlayerNumber: string;
  fouledPlayerNumber: string;
  turnoverOpponentPlayerNumber: string;
  official1Id: OfficialStateAction;
  official2Id: OfficialStateAction;
  assist1PlayerNumber: string;
  assist2PlayerNumber: string;

  selectedTrackId: number;

  detections: Detection[];

  highlightType: HighlightType;
  videoReviewType: VideoReviewType;
  highlightPlayback: HighlightPlayback;
  highlightRating: HighlightRating;

  netImpact: NetImpactPositionCoordinate;

  penaltyType: PenaltyType;
  penaltyId: ObjectId;
  penaltyDuration: PenaltyDuration;

  gameIncidentReason: GameIncidentReason;
  gameIncidentSituation: GameIncidentSituation;
  gameIncidentType: GameIncidentType;
  officialsCallAction: OfficialsCallAction;
  officialsCallSituation: OfficialsCallSituation;
}

export const INITIAL_GAME_EVENT_STATE: GameEventState = {
  _id: null,
  videoTime: 0,
  gameTime: 0,
  period: '1',
  strengthState: '5-5',
  eventType: null,
  team: undefined,
  isHomeTeamEmptyNet: false,
  isAwayTeamEmptyNet: false,
  oddMenRushDetail: undefined,

  position: undefined,
  positionPitch: undefined,
  passReceiverPosition: undefined,
  shotBlockerPosition: undefined,
  faceoffOutcomePosition: undefined,

  passType: undefined,
  passOutcome: undefined,

  shotOutcome: undefined,
  shotScenario: undefined,
  shotType: undefined,
  attackScenario: undefined,

  is_blocked: undefined,
  assist1: undefined,
  assist1Id: undefined,
  assist2: undefined,
  assist2Id: undefined,
  blocker: undefined,
  blockerId: undefined,
  blockerXPosition: undefined,
  blockerYPosition: undefined,
  has_net_traffic: undefined,
  has_screen: undefined,

  teamFaceOffOutcome: undefined,

  interruption_type: undefined,

  videoTag: undefined,
  defensiveReaction: undefined,
  gameRestartType: undefined,
  gameRestartReason: undefined,
  turnoverType: undefined,
  turnoverScenario: undefined,

  playerNumber: undefined,
  passReceiverPlayerNumber: undefined,
  shotBlockerPlayerNumber: undefined,
  deflectorPlayerNumber: undefined,
  screenerPlayerNumber: undefined,
  netTrafficCauserPlayerNumber: undefined,
  faceOffOpponentPlayerNumber: undefined,
  fouledPlayerNumber: undefined,
  turnoverOpponentPlayerNumber: undefined,
  official1Id: undefined,
  official2Id: undefined,
  assist1PlayerNumber: undefined,
  assist2PlayerNumber: undefined,

  selectedTrackId: undefined,

  detections: undefined,

  highlightType: undefined,
  videoReviewType: undefined,
  highlightPlayback: undefined,
  highlightRating: undefined,

  netImpact: undefined,

  penaltyType: undefined,
  penaltyId: undefined,
  penaltyDuration: undefined,

  gameIncidentReason: undefined,
  gameIncidentSituation: undefined,
  gameIncidentType: undefined,
  officialsCallAction: undefined,
  officialsCallSituation: undefined
};

const gameReducer = createReducer(
  INITIAL_GAME_EVENT_STATE,
  on(eventIdChange, (state, { _id }) => ({
    ...state,
    _id
  })),
  on(gameTimeChange, (state, { gameTime }) => ({
    ...state,
    gameTime
  })),
  on(videoTimeChange, (state, { videoTime }) => ({
    ...state,
    videoTime
  })),
  on(periodChange, (state, { period }) => ({
    ...state,
    period
  })),
  on(strengthStateChange, (state, { strengthState }) => ({
    ...state,
    strengthState
  })),
  on(eventTypeChange, (state, { eventType }) => ({
    ...state,
    eventType
  })),
  on(teamChange, (state, { team }) => ({
    ...state,
    team
  })),
  on(isHomeTeamEmptyNetChange, (state, { isHomeTeamEmptyNet }) => ({
    ...state,
    isHomeTeamEmptyNet
  })),
  on(isAwayTeamEmptyNetChange, (state, { isAwayTeamEmptyNet }) => ({
    ...state,
    isAwayTeamEmptyNet
  })),
  on(oddMenRushDetailChange, (state, { oddMenRushDetail }) => ({
    ...state,
    oddMenRushDetail
  })),
  on(positionChange, (state, { position }) => ({
    ...state,
    position
  })),
  on(positionPitchChange, (state, { positionPitch }) => ({
    ...state,
    positionPitch
  })),
  on(passReceiverPositionChange, (state, { passReceiverPosition }) => ({
    ...state,
    passReceiverPosition
  })),
  on(shotBlockerPositionChange, (state, { shotBlockerPosition }) => ({
    ...state,
    shotBlockerPosition
  })),
  on(faceoffOutcomePositionChange, (state, { faceoffOutcomePosition }) => ({
    ...state,
    faceoffOutcomePosition
  })),
  on(passTypeChange, (state, { passType }) => ({
    ...state,
    passType
  })),
  on(passOutcomeChange, (state, { passOutcome }) => ({
    ...state,
    passOutcome
  })),
  on(shotOutcomeChange, (state, { shotOutcome }) => ({
    ...state,
    shotOutcome
  })),
  on(attackScenarioChange, (state, { attackScenario }) => ({
    ...state,
    attackScenario
  })),
  on(shotScenarioChange, (state, { shotScenario }) => ({
    ...state,
    shotScenario
  })),
  on(shotTypeChange, (state, { shotType }) => ({
    ...state,
    shotType
  })),
  on(isBlockedChange, (state, { is_blocked }) => ({
    ...state,
    is_blocked
  })),
  on(hasNetTrafficChange, (state, { has_net_traffic }) => ({
    ...state,
    has_net_traffic
  })),
  on(hasScreenChange, (state, { has_screen }) => ({
    ...state,
    has_screen
  })),
  on(teamFaceoffOutcomeChange, (state, { teamFaceOffOutcome }) => ({
    ...state,
    teamFaceOffOutcome
  })),
  on(interruptionTypeChange, (state, { interruption_type }) => ({
    ...state,
    interruption_type
  })),
  on(videoTagChange, (state, { videoTag }) => ({
    ...state,
    videoTag
  })),
  on(defensiveReactionChange, (state, { defensiveReaction }) => ({
    ...state,
    defensiveReaction
  })),
  on(gameRestartTypeChange, (state, { gameRestartType }) => ({
    ...state,
    gameRestartType
  })),
  on(gameRestartReasonChange, (state, { gameRestartReason }) => ({
    ...state,
    gameRestartReason
  })),
  on(turnoverTypeChange, (state, { turnoverType }) => ({
    ...state,
    turnoverType
  })),
  on(turnoverScenarioChange, (state, { turnoverScenario }) => ({
    ...state,
    turnoverScenario
  })),
  on(playerNumberChange, (state, { playerNumber }) => ({
    ...state,
    playerNumber
  })),
  on(passReceiverPlayerNumberChange, (state, { passReceiverPlayerNumber }) => ({
    ...state,
    passReceiverPlayerNumber
  })),
  on(assist1PlayerNumberChange, (state, { assist1PlayerNumber }) => ({
    ...state,
    assist1PlayerNumber
  })),
  on(assist2PlayerNumberChange, (state, { assist2PlayerNumber }) => ({
    ...state,
    assist2PlayerNumber
  })),
  on(shotBlockerPlayerNumberChange, (state, { shotBlockerPlayerNumber }) => ({
    ...state,
    shotBlockerPlayerNumber
  })),
  on(deflectorPlayerNumberChange, (state, { deflectorPlayerNumber }) => ({
    ...state,
    deflectorPlayerNumber
  })),
  on(screenerPlayerNumberChange, (state, { screenerPlayerNumber }) => ({
    ...state,
    screenerPlayerNumber
  })),
  on(
    netTrafficCauserPlayerNumberChange,
    (state, { netTrafficCauserPlayerNumber }) => ({
      ...state,
      netTrafficCauserPlayerNumber
    })
  ),
  on(
    faceoffOpponentPlayerNumberChange,
    (state, { faceOffOpponentPlayerNumber }) => ({
      ...state,
      faceOffOpponentPlayerNumber
    })
  ),
  on(fouledPlayerNumberChange, (state, { fouledPlayerNumber }) => ({
    ...state,
    fouledPlayerNumber
  })),
  on(
    turnoverOpponentPlayerNumberChange,
    (state, { turnoverOpponentPlayerNumber }) => ({
      ...state,
      turnoverOpponentPlayerNumber
    })
  ),
  on(official1NumberChange, (state, { officialId, remove }) => ({
    ...state,
    official1Id: { officialId, remove: remove || false }
  })),
  on(official2NumberChange, (state, { officialId, remove }) => ({
    ...state,
    official2Id: { officialId, remove: remove || false }
  })),
  on(selectedTrackIdChange, (state, { selectedTrackId }) => ({
    ...state,
    selectedTrackId
  })),
  on(detectionsChange, (state, { detections }) => ({
    ...state,
    detections
  })),
  on(highlightTypeChange, (state, { highlightType }) => ({
    ...state,
    highlightType
  })),
  on(videoReviewTypeChange, (state, { videoReviewType }) => ({
    ...state,
    videoReviewType
  })),
  on(highlightPlaybackChange, (state, { highlightPlayback }) => ({
    ...state,
    highlightPlayback
  })),
  on(highlightRatingChange, (state, { highlightRating }) => ({
    ...state,
    highlightRating
  })),
  on(netImpactChange, (state, { netImpact }) => ({
    ...state,
    netImpact
  })),
  on(penaltyTypeChange, (state, { penaltyType }) => ({
    ...state,
    penaltyType
  })),
  on(penaltyIdChange, (state, { penaltyId }) => ({
    ...state,
    penaltyId
  })),
  on(penaltyDurationChange, (state, { penaltyDuration }) => ({
    ...state,
    penaltyDuration
  })),
  on(gameIncidentReasonChange, (state, { gameIncidentReason }) => ({
    ...state,
    gameIncidentReason
  })),
  on(gameIncidentSituationChange, (state, { gameIncidentSituation }) => ({
    ...state,
    gameIncidentSituation
  })),
  on(gameIncidentTypeChange, (state, { gameIncidentType }) => ({
    ...state,
    gameIncidentType
  })),
  on(officialsCallActionChange, (state, { officialsCallAction }) => ({
    ...state,
    officialsCallAction
  })),
  on(officialsCallSituationChange, (state, { officialsCallSituation }) => ({
    ...state,
    officialsCallSituation
  })),
  on(resetState, (state, payload) => {
    let result = Object.assign({}, INITIAL_GAME_EVENT_STATE, {
      gameTime: state.gameTime,
      videoTime: state.videoTime,
      strengthState: state.strengthState,
      isHomeTeamEmptyNet: state.isHomeTeamEmptyNet,
      isAwayTeamEmptyNet: state.isAwayTeamEmptyNet
    });

    if (payload.period) {
      result = Object.assign({}, result, { period: payload.period });
    }

    if (payload.team) {
      result = Object.assign({}, result, { team: payload.team });
    }

    return result;
  }),
  on(resetHardState, (state, payload) =>
    Object.assign({}, INITIAL_GAME_EVENT_STATE)
  )
);

const gameFeature = createFeature({
  name: gameEventFeatureKey,
  reducer: gameReducer
});

export const {
  name,
  reducer,
  select_id,
  selectGameTime,
  selectVideoTime,
  selectPeriod,
  selectStrengthState,
  selectEventType,
  selectTeam,
  selectIsHomeTeamEmptyNet,
  selectIsAwayTeamEmptyNet,
  selectOddMenRushDetail,
  selectPosition,
  selectPositionPitch,
  selectPassReceiverPosition,
  selectShotBlockerPosition,
  selectFaceoffOutcomePosition,
  selectPassType,
  selectPassOutcome,
  selectShotOutcome,
  selectShotScenario,
  selectShotType,
  selectIs_blocked,
  selectHas_net_traffic,
  selectHas_screen,
  selectTeamFaceOffOutcome,
  selectInterruption_type,
  selectVideoTag,
  selectDefensiveReaction,
  selectGameRestartType,
  selectGameRestartReason,
  selectTurnoverType,
  selectTurnoverScenario,
  selectPlayerNumber,
  selectPassReceiverPlayerNumber,
  selectShotBlockerPlayerNumber,
  selectDeflectorPlayerNumber,
  selectScreenerPlayerNumber,
  selectNetTrafficCauserPlayerNumber,
  selectFaceOffOpponentPlayerNumber,
  selectFouledPlayerNumber,
  selectTurnoverOpponentPlayerNumber,
  selectOfficial1Id,
  selectOfficial2Id,
  selectSelectedTrackId,
  selectDetections,
  selectHighlightType,
  selectHighlightPlayback,
  selectHighlightRating,
  selectNetImpact,
  selectPenaltyType,
  selectPenaltyId,
  selectPenaltyDuration,
  selectAssist1PlayerNumber,
  selectAssist2PlayerNumber,
  selectGameIncidentReason,
  selectGameIncidentSituation,
  selectGameIncidentType,
  selectOfficialsCallAction,
  selectOfficialsCallSituation,
  selectGameEventState,
  selectVideoReviewType,
  selectAttackScenario
} = gameFeature;
